'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useCartFlightState } from '@/hooks/use-cart-flight';
import { cartIndicatorView, settleCartIndicatorBaseline } from '@/lib/cart-flight';

/**
 * A count badge on an icon control: the cart's items, unread notifications.
 * One of the brand's jobs (DESIGN.md, The Brand Jobs Rule): the accent fill
 * with its contrast-picked label, a ring in the page colour so it reads where
 * it overlaps the icon. 12px numerals, the type floor.
 */
export const COUNT_BADGE =
  'absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-xs font-semibold leading-none tabular-nums text-brand-foreground ring-2 ring-background';

/** What a screen reader hears for the cart control. */
export function cartLabel(count: number): string {
  if (count <= 0) return 'Cart';
  return `Cart, ${count} item${count === 1 ? '' : 's'}`;
}

/**
 * The cart control's count badge, and the flash when an add-to-cart star lands
 * on it: white on the dark sky, the accent in light mode (white does not show
 * on a light page). Shared by both of the header's cart branches and the
 * storefront's cart link, so the three stay identical (a buyer who has seen one
 * badge should recognise the others).
 *
 * Render it as a direct child of the control's `relative` box: the badge is
 * positioned against it and the flash takes its corner radius by inheriting.
 *
 * With no flight in play the badge shows whenever the cart has items, no
 * animation. A flight changes two things (see src/lib/cart-flight.ts):
 *   - for a first item the badge is held back while the star is in the air,
 *     then grows in from nothing as it lands;
 *   - for a later item the badge stays put and swells slightly on landing.
 * Either way the control flashes and fades back.
 */
export function CartIndicator({ hasItems, count }: { hasItems: boolean; count: number }) {
  const { holdDot, arrivals, lastArrival } = useCartFlightState();

  // The arrival count is global, so each control keeps the count it has
  // already settled and only a landing past it animates. The keys and the
  // settling rule live in cart-flight.ts, where node:test can walk them.
  const baseline = useRef(arrivals);
  const view = cartIndicatorView(hasItems, { holdDot, arrivals, lastArrival }, baseline.current);
  useEffect(() => {
    baseline.current = settleCartIndicatorBaseline(hasItems, holdDot, arrivals, baseline.current);
  }, [hasItems, holdDot, arrivals]);

  return (
    <>
      {view.dot && (
        <span
          key={view.dotKey}
          aria-hidden
          className={cn(
            COUNT_BADGE,
            view.dotAnimation === 'reveal' && 'animate-cart-dot-reveal',
            view.dotAnimation === 'bump' && 'animate-cart-dot-bump',
          )}
        >
          {count > 9 ? '9+' : count}
        </span>
      )}
      {view.glowKey !== null && (
        <span
          key={view.glowKey}
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] bg-primary/15 opacity-0 shadow-[0_0_16px_4px_hsl(var(--primary)/0.45)] dark:bg-white/25 dark:shadow-[0_0_16px_4px_rgba(255,255,255,0.6)] animate-cart-arrival-glow"
        />
      )}
    </>
  );
}
